// src/assets/js/shared/favorites-apply.js
const DEFAULT_STORAGE_KEY = 'selectivia:favs';
const MAX_FAVS = 9;

function safeParse(json, fallback) {
	try {
		return JSON.parse(json);
	} catch {
		return fallback;
	}
}

function loadFavs(storageKey = DEFAULT_STORAGE_KEY) {
	const raw = localStorage.getItem(storageKey);
	const data = safeParse(raw, { quizzes: [] });
	const quizzes = Array.isArray(data?.quizzes) ? data.quizzes : [];
	return quizzes
		.map((q) => ({
			quizId: String(q?.quizId || '').trim(),
			subjectId: String(q?.subjectId || '').trim(),
			subjectTitle: String(q?.subjectTitle || '').trim(),
			quizTitle: String(q?.quizTitle || '').trim(),
		}))
		.filter((q) => q.quizId);
}

function saveFavs(storageKey, favs) {
	localStorage.setItem(storageKey, JSON.stringify({ quizzes: favs }));
}

function setStarIcon(btn, isFav) {
	btn.classList.toggle('is-fav', isFav);
	btn.setAttribute('aria-pressed', String(isFav));
}

function readDataset(btn) {
	return {
		quizId: String(btn.dataset.quizId || '').trim(),
		subjectId: String(btn.dataset.subjectId || '').trim(),
		subjectTitle: String(btn.dataset.subjectTitle || '').trim(),
		quizTitle: String(btn.dataset.quizTitle || '').trim(),
	};
}

/**
 * API tipo "apply()": pintas favoritos tras cada render.
 * - initFavorites({ root, storageKey }).apply()
 * - Delegación de click para [data-action="fav-quiz"]
 */
export function initFavorites({ root = document, storageKey = DEFAULT_STORAGE_KEY } = {}) {
	if (!root) root = document;

	function getFavSet() {
		const favs = loadFavs(storageKey);
		return new Set(favs.map((f) => f.quizId));
	}

	function apply(scope = root) {
		const favSet = getFavSet();

		scope.querySelectorAll('[data-action="fav-quiz"]').forEach((btn) => {
			const quizId = String(btn.dataset.quizId || '').trim();

			if (!quizId) {
				btn.disabled = true;
				setStarIcon(btn, false);
				return;
			}

			btn.disabled = false;
			setStarIcon(btn, favSet.has(quizId));
		});
	}

	function toggleEverywhere(quizId) {
		const qid = String(quizId || '').trim();
		if (!qid) return;

		const favSet = getFavSet();
		root
			.querySelectorAll(`[data-action="fav-quiz"][data-quiz-id="${CSS.escape(qid)}"]`)
			.forEach((btn) => setStarIcon(btn, favSet.has(qid)));
	}

	function onClick(e) {
		const btn = e.target?.closest?.('[data-action="fav-quiz"]');
		if (!btn || !root.contains(btn)) return;

		// Importante: que NO dispare el click del botón "open-quiz"
		e.preventDefault();
		e.stopPropagation();

		const { quizId, subjectId, subjectTitle, quizTitle } = readDataset(btn);
		if (!quizId) return;

		let favs = loadFavs(storageKey);
		const favSet = new Set(favs.map((f) => f.quizId));

		const isFav = favSet.has(quizId);

		if (isFav) {
			favs = favs.filter((f) => f.quizId !== quizId);
			saveFavs(storageKey, favs);
			toggleEverywhere(quizId);
			return;
		}

		if (favSet.size >= MAX_FAVS) {
			// aquí si quieres puedes reutilizar tu shake()
			return;
		}

		favs = [...favs.filter((f) => f.quizId !== quizId), { quizId, subjectId, subjectTitle, quizTitle }];
		saveFavs(storageKey, favs);
		toggleEverywhere(quizId);
	}

	// Evita doble bind
	if (!root.__favoritesApplyBound__) {
		root.addEventListener('click', onClick);
		root.__favoritesApplyBound__ = true;
	}

	// Pintado inicial (por si ya hay botones en DOM)
	apply(root);

	return { apply };
}
